import { useEffect, useRef, useState } from 'react';

const POLLING_ATTEMPTS = 4;
const POLLING_INTERVAL_MS = 1000;

const waitForNextAttempt = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, POLLING_INTERVAL_MS);
  });

const usePaymentOptionsPolling = (refetch: () => Promise<void>) => {
  const [isPolling, setIsPolling] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [error, setError] = useState<Error>();
  const pollingInProgressRef = useRef(false);
  const cancelledRef = useRef(false);

  const startPolling = async () => {
    if (pollingInProgressRef.current) {
      return;
    }
    cancelledRef.current = false;
    setHasCompleted(false);
    setError(undefined);

    pollingInProgressRef.current = true;
    setIsPolling(true);

    try {
      for (let attempt = 1; attempt <= POLLING_ATTEMPTS; attempt += 1) {
        if (cancelledRef.current) {
          return;
        }

        try {
          await refetch();
        } catch (error) {
          if (attempt === POLLING_ATTEMPTS) {
            setError(error instanceof Error ? error : new Error(String(error)));
            return;
          }
        }

        if (attempt < POLLING_ATTEMPTS && !cancelledRef.current) {
          // Await each response before delaying to prevent overlapping requests.
          await waitForNextAttempt();
        }
      }

      if (cancelledRef.current) {
        return;
      }

      setHasCompleted(true);
    } finally {
      pollingInProgressRef.current = false;
      if (!cancelledRef.current) {
        setIsPolling(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      pollingInProgressRef.current = false;
      cancelledRef.current = true;
    };
  }, []);

  return {
    startPolling,
    isPolling,
    hasCompleted,
    error,
  };
};
export default usePaymentOptionsPolling;
